import { DataItem } from 'scrivito'

import { Address } from '@/Data/Address/AddressDataClass'
import { AddressContactRelation } from '@/Data/AddressContactRelation/AddressContactRelationDataClass'
import { reportError } from '@/utils/reportError'

interface UseAddressTabDataArgs {
  subjectId: string
  limit: number
  search: string
  currentDrawerId?: string
}

const ADDRESS_ORDER: [string, 'asc' | 'desc'][] = [
  ['type', 'asc'],
  ['street', 'asc'],
]

const getAddressIdFromRelation = (relation: DataItem) =>
  (relation.get('address') as string | null) || relation.id()

export const useAddressTabData = ({
  subjectId,
  limit,
  search,
  currentDrawerId,
}: UseAddressTabDataArgs) => {
  let relationItems: DataItem[] = []
  let addresses: DataItem[] = []
  let searchResultAddresses: DataItem[] = []
  let allAddressRelations: DataItem[] = []
  let count = 0
  let loadMore = false

  try {
    if (subjectId) {
      const relationScope = AddressContactRelation.all().transform({
        filters: { contact: subjectId },
        limit,
      })
      relationItems = relationScope.take()
      count = relationScope.count() || 0
      loadMore = count > relationItems.length
    }

    const addressIds = relationItems.map(getAddressIdFromRelation)

    if (addressIds.length) {
      addresses = Address.all()
        .transform({
          filters: { ids: addressIds.join(',') },
          order: ADDRESS_ORDER,
          limit: addressIds.length,
        })
        .take()
    }

    if (search && subjectId) {
      allAddressRelations = AddressContactRelation.all()
        .transform({ filters: { contact: subjectId } })
        .take()

      const allAddressIds = allAddressRelations.map(getAddressIdFromRelation)
      if (allAddressIds.length) {
        searchResultAddresses = Address.all()
          .transform({
            filters: { ids: allAddressIds.join(',') },
            limit: allAddressIds.length,
            search,
            order: ADDRESS_ORDER,
          })
          .take()
      }
    }
  } catch (error) {
    reportError(error)
  }

  const findRelationForAddress = (addressId: string) =>
    (search ? allAddressRelations : relationItems).find(
      (relation) =>
        getAddressIdFromRelation(relation) === addressId ||
        relation.id() === addressId,
    )

  const localSearch = search.trim().toLowerCase()

  const listAddresses = search
    ? Array.from(
        new Map(
          [...addresses, ...searchResultAddresses].map((a) => [a.id(), a]),
        ).values(),
      )
    : addresses

  const listAddressIds = listAddresses.map((a) => a.id())
  const currentIndex = listAddressIds.findIndex((id) => currentDrawerId === id)
  const nextId = listAddressIds[currentIndex + 1]
  const previousId = listAddressIds[currentIndex - 1]

  return {
    listAddresses,
    localSearch,
    findRelationForAddress,
    nextId,
    previousId,
    count,
    loadMore,
  }
}
